import { KVRow, SectionCard } from '@components';
import { EthernetPort, TableConfig } from 'lucide-react';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { Wifi } from 'react-feather';
import { MdCyclone, MdNetworkPing } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import LoadingSpinner from '../../../components/buttons/LoadingSpinner';
import NetworkDeviceInterfaces from '../../../components/charts/stats/NetworkDeviceInterfaces';
import NetworkRtdSummary from '../../../components/charts/stats/NetworkRtdSummary';
import { RtdChartVisualization } from '../../../components/charts/stats/NeworkDeviceRtd';
import NetworkRtdTable from '../../../components/tables/NetworkRtdTable';
import Tabs from '../../../components/tabs/Tabs';
import NetworkPortTag, {
  COMMON_NETWORK_PORTS,
} from '../../../components/tags/NetworkPortTag';
import Tag from '../../../components/tags/Tag';
import Typography from '../../../components/typography/Typography';
import {
  DomotzDevice,
  DomotzNetworkInterface,
  DomotzRtd,
  DomotzRtdentry,
  DomotzUptimeData,
} from '../../../interfaces/NetworkDevices';
import { NetworkDevicesService } from '../../../services/assets/NetworkDevices';
import { setRootAside } from '../../../state/component/AsideSlice';
import { RootState } from '../../../state/store';
import ListComponent from '../../../utils/ListComponent';
import { formatToUppercaseWithHyphens } from '../../../utils/text/TruncateString';

interface props {
  data: { device: DomotzDevice };
}

interface detailItem {
  label: string;
  value: string | null | undefined | number | ReactElement;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'ONLINE':
      return 'success';
    case 'OFFLINE':
      return 'offline';
    case 'VITAL':
      return 'offline';
    case 'HIGH':
      return 'bussy';
    case 'FLOATING':
      return 'bussy';
    case 'MEDIUM':
      return 'neutral';
    default:
      return 'neutral';
  }
};

const DetailItem: FC<detailItem> = ({ label, value }) => (
  <div className="flex justify-between items-center py-1">
    <Typography tag="span" type="labelSemiBold">
      {label}
    </Typography>
    <Typography tag="span" type="label">
      {value === null || value === '' ? 'N/A' : value}
    </Typography>
  </div>
);

export const NetworkDeviceDetailContainer: FC<props> = ({ data }) => {
  const networkDeviceService = new NetworkDevicesService();
  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );
  const [loadingConnection, setLoadingConnection] = useState<boolean>(false);
  const [externalLink, setExternalLink] = useState<string>('');
  const [assetImageUrl, setAssetImageUrl] = useState<string>('');
  const [deviceUptime, setDeviceUptime] = useState<DomotzUptimeData>();
  const [domotzVariables, setDomotzVariables] = useState<
    DomotzNetworkInterface[]
  >([]);

  const dispatch = useDispatch();
  const [rtd, setRtd] = useState<DomotzRtd>();

  if (!companyId) {
    return 'error';
  }

  const setDetail = (device: DomotzDevice) => {
    dispatch(
      setRootAside({
        component: 'networkDeviceDetailContainer',
        data: { device },
        title: 'Network device',
      })
    );
  };

  const getRdtHistory = async () => {
    const rtd = await networkDeviceService.getRtdHistory(
      data.device.id,
      companyId
    );

    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 48 * 60 * 60 * 1000);

    const newDataSet = rtd.history.filter((entry: DomotzRtdentry) => {
      const entryTime = new Date(entry.timestamp);
      return entryTime >= twentyFourHoursAgo;
    });

    setRtd(newDataSet);
  };

  const connectToDevice = async (port: number): Promise<void> => {
    setLoadingConnection(true);
    const ip = await networkDeviceService.getPublicIp();

    const body = {
      port: port,
      allowed_ip: ip.data.ip,
      protocol: COMMON_NETWORK_PORTS[port].toLowerCase(),
      ttl_hours: 1,
    };

    const connection = await networkDeviceService.generateNetworkConnection(
      companyId,
      data.device.id,
      body
    );
    setLoadingConnection(false);
    setExternalLink(connection.connection.link);
  };

  const getAssetUrl = async () => {
    const model = data.device.user_data?.model ?? (data.device.model as string);

    if (model) {
      const formatedModel = formatToUppercaseWithHyphens(model);
      try {
        const result = await networkDeviceService.getAssetImageUrl(
          companyId,
          formatedModel
        );
        setAssetImageUrl(result.url);
      } catch (error) {
        console.error('Failed to fetch asset image URL:', error);
        setAssetImageUrl('noUrl');
      }
    }
  };

  const getDeviceVariables = async () => {
    const queryParams = {
      path: 'ifOperStatus',
    };
    const response = await networkDeviceService.getDeviceVariables(
      data.device.id,
      companyId,
      queryParams
    );
    setDomotzVariables(response.variables);
  };

  const getDeviceUptime = async () => {
    const queryParams = {
      path: 'sysUpTime',
    };
    const response = await networkDeviceService.getDeviceVariables(
      data.device.id,
      companyId,
      queryParams
    );
    setDeviceUptime(response.variables[0]);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusClasses = getStatusColor(data.device.status);
  const importanceClasses = getStatusColor(data.device.importance);

  const tabs = [
    {
      nav: 'RTD Chart',
      component: [<RtdChartVisualization history={rtd} />],
    },
    {
      nav: 'RTD Table',
      component: [<NetworkRtdTable history={rtd} />],
    },
    {
      nav: 'Interfaces',
      component: [<NetworkDeviceInterfaces data={domotzVariables} />],
    },
  ];

  useEffect(() => {
    getRdtHistory();
    getAssetUrl();
    getDeviceVariables();
    getDeviceUptime();
  }, []);

  return (
    <div className="mx-auto">
      <div className="flex justify-between items-start border-b pb-4 mb-4 border-gray-100">
        <div>
          <Typography tag="h1" type="title">
            {data.device.display_name}
          </Typography>

          <Typography tag="p" type="faded">
            {data.device.type.label} ({data.device.vendor}{' '}
            {data.device.user_data?.model ?? (data.device.model as string)})
          </Typography>
        </div>

        <div className="flex flex-row space-x-2 justify-end">
          <Tag label={data.device.status} style={statusClasses} />
          <Tag label={data.device.importance} style={importanceClasses} />
        </div>
      </div>

      {assetImageUrl && assetImageUrl !== 'noUrl' ? (
        <img src={assetImageUrl} alt="Asset" />
      ) : assetImageUrl === 'noUrl' ? null : (
        <LoadingSpinner />
      )}

      <div className="grid grid-cols-2 md:grid-cols-2 gap-6 my-4">
        <div className="col-span-1">
          <SectionCard
            title="Identity & Network"
            icon={<EthernetPort size={13} />}
          >
            <KVRow
              label="Model"
              value={
                data.device.user_data?.model ?? (data.device.model as string)
              }
            />
            <KVRow
              label="IP Address"
              value={data.device.ip_addresses.join(', ')}
            />
            <KVRow label="Protocol" value={data.device.protocol} />
            <KVRow label="SNMP Status" value={data.device.snmp_status} />
          </SectionCard>
        </div>

        <div className="col-span-1">
          <SectionCard
            title="Status & Lifecycle"
            icon={<MdCyclone size={13} />}
          >
            <KVRow
              label="Last Status Change"
              value={formatDate(data.device.last_status_change)}
            />
            <KVRow
              label="Agent Reachable"
              value={data.device.agent_reachable ? 'Yes' : 'No'}
            />
            <KVRow
              label="Uptime"
              value={`${deviceUptime?.value} ${deviceUptime?.unit}(s)`}
            />
            <KVRow label="Serial Number" value={data.device.details.serial} />
          </SectionCard>
        </div>

        <div className="col-span-1 ">
          <SectionCard title="Configuration" icon={<TableConfig size={13} />}>
            {loadingConnection ? (
              <>
                <KVRow
                  label="Generating secure link"
                  value={<LoadingSpinner />}
                />
              </>
            ) : externalLink ? (
              <>
                <KVRow
                  label="Generating secure link"
                  value={
                    <a
                      href={externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        setExternalLink('');
                      }}
                    >
                      <Tag label="connect" style="blue" />
                    </a>
                  }
                />
              </>
            ) : (
              <>
                <KVRow
                  label="TCP Ports"
                  value={
                    <div className="flex flex-wrap items-center gap-2">
                      <ListComponent
                        data={data.device.open_ports.tcp}
                        renderItem={(tcp) => (
                          <NetworkPortTag
                            port={tcp}
                            onclick={() => connectToDevice(tcp)}
                          />
                        )}
                      />
                    </div>
                  }
                />
              </>
            )}

            <KVRow label="Grace Period (s)" value={data.device.grace_period} />
            <KVRow label="Location Zone" value={data.device.details.zone} />
            <KVRow label="Room" value={data.device.details.room} />
          </SectionCard>
        </div>
        <div className="col-span-1">
          <SectionCard
            title="Discovered Network Devices"
            icon={<Wifi size={13} />}
          >
            {data.device.uplink && data.device.uplink.length > 0 ? (
              <ListComponent
                data={data.device.uplink}
                renderItem={(device) => (
                  <div>
                    <KVRow label={device.type} value={device.display_name} />
                  </div>
                )}
              />
            ) : (
              'No connections found'
            )}
          </SectionCard>
        </div>
      </div>

      <SectionCard title="RTD" icon={<MdNetworkPing size={13} />}>
        <NetworkRtdSummary history={rtd} />
        <div>{rtd ? <Tabs tabs={tabs} /> : <LoadingSpinner />}</div>

        <div className="mt-6 pt-4 border-t border-gray-100">
          {data.device.is_excluded && (
            <div className="mt-3 p-3 text-sm font-medium bg-amber-100 text-amber-700 rounded border border-amber-300">
              This device is currently excluded from standard monitoring.
            </div>
          )}
        </div>
      </SectionCard>
    </div>
  );
};

export default NetworkDeviceDetailContainer;
